import React, { useState, useEffect, useRef, useMemo } from 'react';
import { DeepLinkConfig, Environment, generateDeepLinkUrl, validateDeepLinkConfig } from '../services/DeepLinkService.ts';
import { loadStoreProducts, extractCategories, extractBrands, extractBrandsByCategories, extractSubcategoriesByCategory, extractWeights, searchProducts, formatProductDisplay, getProductWeights, getProductById } from '../services/AdminMenuService.ts';
import { MenuProduct } from '../services/MenuService.ts';
import ProductPicker from './ProductPicker.tsx';
import './DeepLinkConfigurator.css';

// Interface for Dutchie special/deal data
interface DutchieSpecial {
  id: string;
  name: string;
  type: 'SALE' | 'OFFER';
  menuType?: 'BOTH' | 'MEDICAL' | 'RECREATIONAL';
  description?: string;
  schedule?: {
    startStamp?: string;  // ISO timestamp for the deal's go-live
    endStamp?: string;
    days?: number[];
    recurringStartTime?: string;
    recurringEndTime?: string;
  };
}

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL!;

/**
 * Auto-detect environment for deep link generation
 * Uses REACT_APP_ENV to determine if we should generate QA or production links
 */
const getEnvironment = (): Environment => {
  const env = process.env.REACT_APP_ENV;
  // Use QA URLs for qa, development, and undefined (local) environments
  // Only use production URLs when explicitly set to 'production'
  return env === 'production' ? 'production' : 'qa';
};

interface StoreContext {
  storeId: string; // "maryland/olney"
  menuType: 'medical' | 'recreational';
  retailerId: string;
  legacyMenuId: string;
}

interface DeepLinkConfiguratorProps {
  storeContext: StoreContext;
  initialConfig?: DeepLinkConfig | null;
  onChange: (config: DeepLinkConfig | null) => void;
}

const DeepLinkConfigurator: React.FC<DeepLinkConfiguratorProps> = ({
  storeContext,
  initialConfig,
  onChange
}) => {
  // Product data
  const [products, setProducts] = useState<MenuProduct[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [allBrands, setAllBrands] = useState<string[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Configuration state
  const [linkType, setLinkType] = useState<DeepLinkConfig['linkType']>(initialConfig?.linkType || null);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(initialConfig?.categories || []);
  const [selectedSubcategories, setSelectedSubcategories] = useState<string[]>(initialConfig?.subcategories || []);
  const [selectedBrands, setSelectedBrands] = useState<string[]>(initialConfig?.brands || []);
  const [selectedWeights, setSelectedWeights] = useState<string[]>(initialConfig?.weights || []);
  const [selectedProductId, setSelectedProductId] = useState<string>(initialConfig?.productId || '');
  const [selectedSpecialId, setSelectedSpecialId] = useState<string>(initialConfig?.specialId || '');

  // Product search (legacy - kept for backwards compat if needed)
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [productSearchResults, setProductSearchResults] = useState<MenuProduct[]>([]);
  const [showProductResults, setShowProductResults] = useState(false);

  // Product map for ProductPicker
  const productMap = useMemo(() => {
    const map = new Map<string, MenuProduct>();
    products.forEach(p => map.set(p.id, p));
    return map;
  }, [products]);

  // UI state
  const [showCopyToast, setShowCopyToast] = useState(false);

  // Ref to store onChange callback to avoid dependency issues
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Ref to track last config JSON to avoid duplicate onChange calls
  const lastConfigJsonRef = useRef<string | null>(null);

  // Dutchie deals state
  const [dutchieDeals, setDutchieDeals] = useState<DutchieSpecial[]>([]);
  const [loadingDeals, setLoadingDeals] = useState(false);
  const [dealsError, setDealsError] = useState<string | null>(null);

  // Fetch Dutchie deals when special link type is selected
  useEffect(() => {
    if (linkType === 'special' && storeContext.legacyMenuId) {
      const fetchDeals = async () => {
        setLoadingDeals(true);
        setDealsError(null);
        try {
          const response = await fetch(`${API_BASE_URL}/api/deals/${storeContext.legacyMenuId}/all`);
          if (!response.ok) {
            throw new Error('Failed to fetch deals');
          }
          const data = await response.json();
          if (data.success && Array.isArray(data.data)) {
            setDutchieDeals(data.data);
          } else {
            setDutchieDeals([]);
          }
        } catch (err) {
          console.error('[DeepLinkConfigurator] Error fetching Dutchie deals:', err);
          setDealsError(err instanceof Error ? err.message : 'Failed to load deals');
          setDutchieDeals([]);
        } finally {
          setLoadingDeals(false);
        }
      };
      fetchDeals();
    }
  }, [linkType, storeContext.legacyMenuId]);

  // Load products on mount
  useEffect(() => {
    const loadProducts = async () => {
      setLoadingProducts(true);
      setLoadError(null);

      try {
        const loadedProducts = await loadStoreProducts(storeContext.retailerId);
        setProducts(loadedProducts);
        setCategories(extractCategories(loadedProducts));
        setAllBrands(extractBrands(loadedProducts));
      } catch (error) {
        console.error('[DeepLinkConfigurator] Error loading products:', error);
        setLoadError(error instanceof Error ? error.message : 'Failed to load products');
      } finally {
        setLoadingProducts(false);
      }
    };

    loadProducts();
  }, [storeContext.retailerId]);

  // Populate product search query when editing existing product link
  useEffect(() => {
    if (initialConfig?.linkType === 'product' && initialConfig.productId && products.length > 0) {
      const product = getProductById(initialConfig.productId, products);
      if (product) {
        setProductSearchQuery(formatProductDisplay(product));
      }
    }
  }, [products, initialConfig]);

  // Filter brands based on selected categories
  const availableBrands = React.useMemo(() => {
    if (selectedCategories.length === 0) {
      return allBrands;
    }

    // Filter products by categories, then extract brands
    return extractBrandsByCategories(products, selectedCategories);
  }, [selectedCategories, products, allBrands]);

  // Filter subcategories based on selected categories
  const availableSubcategories = React.useMemo(() => {
    return extractSubcategoriesByCategory(products, selectedCategories.length > 0 ? selectedCategories : undefined);
  }, [selectedCategories, products]);

  // Filter weights based on selected categories and brands
  const availableWeights = React.useMemo(() => {
    // Use extractWeights with categories and brand filters
    return extractWeights(products, selectedCategories.length > 0 ? selectedCategories : undefined, selectedBrands.length > 0 ? selectedBrands : undefined);
  }, [products, selectedCategories, selectedBrands]);

  // Clear selected brands that are no longer available when category changes
  // Skip while products are still loading — otherwise availableBrands is [] and we'd wipe out
  // valid selections from initialConfig before the user even sees them.
  useEffect(() => {
    if (products.length === 0) return;
    if (selectedBrands.length > 0) {
      const validBrands = selectedBrands.filter(brand => availableBrands.includes(brand));
      if (validBrands.length !== selectedBrands.length) {
        setSelectedBrands(validBrands);
      }
    }
  }, [availableBrands, products.length]);

  // Clear selected subcategories that are no longer available when category changes
  useEffect(() => {
    if (products.length === 0) return;
    if (selectedSubcategories.length > 0) {
      const validSubcategories = selectedSubcategories.filter(subcat => availableSubcategories.includes(subcat));
      if (validSubcategories.length !== selectedSubcategories.length) {
        setSelectedSubcategories(validSubcategories);
      }
    }
  }, [availableSubcategories, products.length]);

  // Clear selected weights that are no longer available when category/brands change
  useEffect(() => {
    if (products.length === 0) return;
    if (selectedWeights.length > 0) {
      const validWeights = selectedWeights.filter(weight => availableWeights.includes(weight));
      if (validWeights.length !== selectedWeights.length) {
        setSelectedWeights(validWeights);
      }
    }
  }, [availableWeights, products.length]);

  // Build current config and notify parent
  useEffect(() => {
    if (!linkType) {
      const configJson = 'null';
      if (lastConfigJsonRef.current !== configJson) {
        lastConfigJsonRef.current = configJson;
        onChangeRef.current(null);
      }
      return;
    }

    const config: DeepLinkConfig = {
      storeId: storeContext.storeId,
      menuType: storeContext.menuType,
      linkType,
      categories: linkType === 'filter' ? (selectedCategories.length > 0 ? selectedCategories : undefined) : undefined,
      subcategories: linkType === 'filter' ? (selectedSubcategories.length > 0 ? selectedSubcategories : undefined) : undefined,
      brands: linkType === 'filter' ? (selectedBrands.length > 0 ? selectedBrands : undefined) : undefined,
      weights: linkType === 'filter' ? (selectedWeights.length > 0 ? selectedWeights : undefined) : undefined,
      productId: linkType === 'product' ? selectedProductId : undefined,
      specialId: linkType === 'special' ? selectedSpecialId : undefined
    };

    // Only call onChange if config actually changed (prevents infinite loops)
    const configJson = JSON.stringify(config);
    if (lastConfigJsonRef.current !== configJson) {
      lastConfigJsonRef.current = configJson;
      onChangeRef.current(config);
    }
  }, [linkType, selectedCategories, selectedSubcategories, selectedBrands, selectedWeights, selectedProductId, selectedSpecialId, storeContext.storeId, storeContext.menuType]);

  // Handle product search
  useEffect(() => {
    if (productSearchQuery.trim() === '') {
      setProductSearchResults([]);
      return;
    }

    const results = searchProducts(productSearchQuery, products);
    setProductSearchResults(results);
  }, [productSearchQuery, products]);

  // Generate current URL
  const getCurrentUrl = (): string => {
    if (!linkType) return '';

    const config: DeepLinkConfig = {
      storeId: storeContext.storeId,
      menuType: storeContext.menuType,
      linkType,
      categories: selectedCategories.length > 0 ? selectedCategories : undefined,
      subcategories: selectedSubcategories.length > 0 ? selectedSubcategories : undefined,
      brands: selectedBrands.length > 0 ? selectedBrands : undefined,
      weights: selectedWeights.length > 0 ? selectedWeights : undefined,
      productId: selectedProductId || undefined,
      specialId: selectedSpecialId || undefined
    };

    const validation = validateDeepLinkConfig(config);
    if (!validation.isValid) {
      return '';
    }

    return generateDeepLinkUrl(config, getEnvironment());
  };

  const handleCopyLink = async () => {
    const url = getCurrentUrl();
    if (!url) return;

    try {
      await navigator.clipboard.writeText(url);
      setShowCopyToast(true);
      setTimeout(() => setShowCopyToast(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      alert('Failed to copy link to clipboard');
    }
  };

  const handleTestLink = () => {
    const url = getCurrentUrl();
    if (!url) return;

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleClear = () => {
    setLinkType(null);
    setSelectedCategories([]);
    setSelectedSubcategories([]);
    setSelectedBrands([]);
    setSelectedWeights([]);
    setSelectedProductId('');
    setSelectedSpecialId('');
    setProductSearchQuery('');
    setProductSearchResults([]);
  };

  const toggleCategory = (category: string) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const toggleSubcategory = (subcategory: string) => {
    setSelectedSubcategories(prev =>
      prev.includes(subcategory)
        ? prev.filter(s => s !== subcategory)
        : [...prev, subcategory]
    );
  };

  const toggleBrand = (brand: string) => {
    setSelectedBrands(prev =>
      prev.includes(brand)
        ? prev.filter(b => b !== brand)
        : [...prev, brand]
    );
  };

  const toggleWeight = (weight: string) => {
    setSelectedWeights(prev =>
      prev.includes(weight)
        ? prev.filter(w => w !== weight)
        : [...prev, weight]
    );
  };

  const selectProduct = (product: MenuProduct) => {
    setSelectedProductId(product.id);
    setProductSearchQuery(formatProductDisplay(product));
    setShowProductResults(false);
  };

  const currentUrl = getCurrentUrl();
  const isValidConfig = currentUrl !== '';

  return (
    <div className="deep-link-configurator">
      <h4>Banner Link Configuration (Optional)</h4>
      <p className="configurator-description">
        Add a link to take users directly to filtered products when they click this banner.
      </p>

      {loadingProducts && (
        <div className="loading-indicator">Loading products...</div>
      )}

      {loadError && (
        <div className="error-message">
          ⚠️ {loadError}
        </div>
      )}

      {!loadingProducts && !loadError && (
        <>
          {/* Link Type Selection */}
          <div className="config-section">
            <label>Link Type:</label>
            <div className="link-type-options">
              <label className="radio-option">
                <input
                  type="radio"
                  data-testid="link-type-filter"
                  checked={linkType === 'filter'}
                  onChange={() => {
                    setLinkType('filter');
                    setSelectedProductId('');
                    setSelectedSpecialId('');
                  }}
                />
                Filter (Category, Subcategory, Brand, and/or Weight)
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  data-testid="link-type-product"
                  checked={linkType === 'product'}
                  onChange={() => {
                    setLinkType('product');
                    setSelectedCategories([]);
                    setSelectedSubcategories([]);
                    setSelectedBrands([]);
                    setSelectedWeights([]);
                    setSelectedSpecialId('');
                  }}
                />
                Specific Product
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  data-testid="link-type-special"
                  checked={linkType === 'special'}
                  onChange={() => {
                    setLinkType('special');
                    setSelectedCategories([]);
                    setSelectedSubcategories([]);
                    setSelectedBrands([]);
                    setSelectedWeights([]);
                    setSelectedProductId('');
                    setProductSearchQuery('');
                  }}
                />
                Dutchie Deal (Special)
              </label>
            </div>
          </div>

          {/* Dynamic selectors based on link type */}
          {linkType && (
            <div className="config-section">
              {/* Filter Mode: Show both Category and Brand selectors */}
              {linkType === 'filter' && (
                <div className="filter-controls-row">
                  {/* Brand Checkboxes (left side) */}
                  <div className="selector-group">
                    <label>Brand(s) (optional):</label>
                    {availableBrands.length > 0 ? (
                      <div className="brand-checkboxes">
                        {availableBrands.map(brand => (
                          <label key={brand} className="checkbox-option">
                            <input
                              type="checkbox"
                              className="checkbox-input"
                              checked={selectedBrands.includes(brand)}
                              onChange={() => toggleBrand(brand)}
                            />
                            <div className="checkbox-custom checkbox-circular"></div>
                            {brand}
                          </label>
                        ))}
                      </div>
                    ) : (
                      <div className="no-brands-message">
                        No brands available for selected category
                      </div>
                    )}
                  </div>

                  {/* Category Selector (right side) */}
                  <div className="selector-group">
                    <label>Categor{categories.length === 1 ? 'y' : 'ies'} (optional):</label>
                    <div className="brand-checkboxes" data-testid="category-checkboxes">
                      {categories.map(cat => (
                        <label key={cat} className="checkbox-option">
                          <input
                            type="checkbox"
                            className="checkbox-input"
                            checked={selectedCategories.includes(cat)}
                            onChange={() => toggleCategory(cat)}
                            data-testid={`category-checkbox-${cat}`}
                          />
                          <div className="checkbox-custom checkbox-circular"></div>
                          {cat}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Subcategory Checkboxes (full width, below brands/category) */}
              {linkType === 'filter' && availableSubcategories.length > 0 && (
                <div className="selector-group" style={{ marginTop: '1rem' }}>
                  <label>Subcategor{availableSubcategories.length === 1 ? 'y' : 'ies'} (optional):</label>
                  <div className="brand-checkboxes">
                    {availableSubcategories.map(subcategory => (
                      <label key={subcategory} className="checkbox-option">
                        <input
                          type="checkbox"
                          className="checkbox-input"
                          checked={selectedSubcategories.includes(subcategory)}
                          onChange={() => toggleSubcategory(subcategory)}
                        />
                        <div className="checkbox-custom checkbox-circular"></div>
                        {subcategory}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Weight Checkboxes (full width, below brands/category) */}
              {linkType === 'filter' && (
                <div className="selector-group" style={{ marginTop: '1rem' }}>
                  <label>Weight(s) (optional):</label>
                  {availableWeights.length > 0 ? (
                    <div className="brand-checkboxes">
                      {availableWeights.map(weight => (
                        <label key={weight} className="checkbox-option">
                          <input
                            type="checkbox"
                            className="checkbox-input"
                            checked={selectedWeights.includes(weight)}
                            onChange={() => toggleWeight(weight)}
                          />
                          <div className="checkbox-custom checkbox-circular"></div>
                          {weight}
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="no-brands-message">
                      No weights available for selected filters
                    </div>
                  )}
                </div>
              )}

              {/* Product Search (using reusable ProductPicker) */}
              {linkType === 'product' && (
                <div className="selector-group">
                  <label>Product:</label>
                  <ProductPicker
                    products={products}
                    productMap={productMap}
                    selectedProductIds={selectedProductId ? [selectedProductId] : []}
                    singleSelect
                    onSelect={(id) => {
                      setSelectedProductId(id);
                    }}
                    onClearSelection={() => {
                      setSelectedProductId('');
                    }}
                    onAdd={() => {}}
                    onRemove={() => {}}
                    searchPlaceholder="Search products..."
                  />
                </div>
              )}

              {/* Dutchie Deal Selector */}
              {linkType === 'special' && (
                <div className="selector-group">
                  <label>Select Dutchie Deal:</label>
                  {loadingDeals ? (
                    <div className="loading-indicator">Loading deals...</div>
                  ) : dealsError ? (
                    <div className="error-message">⚠️ {dealsError}</div>
                  ) : (
                    <>
                      {dutchieDeals.length === 0 ? (
                        <div className="no-deals-message">
                          No Dutchie deals returned for this store.
                          You can still enter a deal ID manually below.
                        </div>
                      ) : (
                        <select
                          data-testid="special-id-select"
                          value={dutchieDeals.some(d => d.id === selectedSpecialId) ? selectedSpecialId : ''}
                          onChange={e => setSelectedSpecialId(e.target.value)}
                          className="deal-select"
                        >
                          <option value="">-- Select a deal --</option>
                          {dutchieDeals.map(deal => {
                            // If the deal has a future startStamp, surface that so the GM
                            // can pre-configure a banner for a deal that hasn't gone live yet.
                            const start = deal.schedule?.startStamp ? new Date(deal.schedule.startStamp) : null;
                            const isUpcoming = start instanceof Date && !isNaN(start.getTime()) && start.getTime() > Date.now();
                            const startLabel = isUpcoming
                              ? ` — Starts ${start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
                              : '';
                            return (
                              <option key={deal.id} value={deal.id}>
                                {deal.name} ({deal.type}){startLabel}
                              </option>
                            );
                          })}
                        </select>
                      )}

                      {/* Manual fallback: Dutchie may filter to active-only depending on
                          retailer config. Let the GM paste a deal ID directly so they
                          aren't blocked while waiting for the deal to go live. */}
                      <div className="manual-deal-id-fallback">
                        <label className="manual-deal-id-label">
                          Don't see your deal? Enter Dutchie deal ID:
                        </label>
                        <input
                          type="text"
                          data-testid="special-id-manual"
                          value={selectedSpecialId}
                          onChange={e => setSelectedSpecialId(e.target.value.trim())}
                          placeholder="e.g. spec_abc123"
                          className="deal-id-manual-input"
                          spellCheck={false}
                        />
                      </div>
                    </>
                  )}
                  {selectedSpecialId && (
                    <p className="input-hint">
                      When users click the banner, the menu will filter to show only products included in this deal.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Generated Link Preview */}
          {linkType && (
            <div className="config-section">
              <label>Generated Link:</label>
              {isValidConfig ? (
                <>
                  <div className="link-preview" data-testid="link-preview">
                    <code>{currentUrl}</code>
                  </div>
                  <div className="link-actions">
                    <button
                      onClick={handleCopyLink}
                      className="copy-button"
                      type="button"
                    >
                      📋 Copy Link
                    </button>
                    <button
                      onClick={handleTestLink}
                      className="test-button"
                      type="button"
                    >
                      🔗 Test in New Tab
                    </button>
                    <button
                      onClick={handleClear}
                      className="clear-button"
                      type="button"
                    >
                      🗑️ Clear
                    </button>
                  </div>
                </>
              ) : (
                <div className="validation-message">
                  Please complete all required fields to generate a link
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Copy Toast */}
      {showCopyToast && (
        <div className="copy-toast">
          ✓ Link copied to clipboard!
        </div>
      )}
    </div>
  );
};

export default DeepLinkConfigurator;
